import {
    CONDITION_FIELD_SOURCE,
    CONDITION_LOGIC,
    CONDITION_OPERATOR,
    EVENT_FIELD_INPUT,
    EVENT_FIELD_OPTIONS,
} from '@constants/automations';
import { MAX_AGE } from '@helpers/contact-demographics';

const { CONTACT_FIELD, CONTACT_LABEL, CUSTOM_EVENT_DATA, TRIGGER_ACTIVITY } = CONDITION_FIELD_SOURCE;
const { EQUALS, NOT_EQUALS, NOT_CONTAINS, EXISTS, NOT_EXISTS, IN, NOT_IN, GREATER_THAN, LESS_THAN } =
    CONDITION_OPERATOR;

export const ROW_KIND = {
    SEGMENT: 'segment',
    FIELD: 'field',
    EVENT: 'event',
};

const RANGE_OPERATORS = [
    { value: GREATER_THAN, key: 'greaterThan' },
    { value: LESS_THAN, key: 'lessThan' },
];

export const EVENT_FIELD_OPERATORS = {
    [EVENT_FIELD_INPUT.SELECT]: [
        { value: IN, key: 'isAnyOf' },
        { value: NOT_IN, key: 'isNoneOf' },
    ],
    [EVENT_FIELD_INPUT.INTEGER]: [{ value: EQUALS, key: 'is' }, ...RANGE_OPERATORS],
    [EVENT_FIELD_INPUT.DECIMAL]: RANGE_OPERATORS,
    [EVENT_FIELD_INPUT.TEXT]: [
        { value: EQUALS, key: 'is' },
        { value: NOT_EQUALS, key: 'isNot' },
    ],
    [EVENT_FIELD_INPUT.BOOLEAN]: [{ value: EQUALS, key: 'is' }],
};

const PRESENCE_OPERATORS = [
    { value: EXISTS, key: 'isKnown' },
    { value: NOT_EXISTS, key: 'isUnknown' },
];

const PICK_OPERATORS = [...EVENT_FIELD_OPERATORS[EVENT_FIELD_INPUT.SELECT], ...PRESENCE_OPERATORS];

export const CONTACT_FIELDS = [
    { value: 'Email', labelKey: 'common.email' },
    { value: 'PhoneNumber', labelKey: 'common.phoneNumber' },
    { value: 'FirstName', labelKey: 'common.firstName' },
    { value: 'LastName', labelKey: 'common.lastName' },
    {
        value: 'Sex',
        labelKey: 'ContactsPage.sex',
        input: EVENT_FIELD_INPUT.SELECT,
        options: EVENT_FIELD_OPTIONS.SEXES,
        operators: PICK_OPERATORS,
    },
    {
        value: 'Language',
        labelKey: 'ContactsPage.language',
        input: EVENT_FIELD_INPUT.SELECT,
        options: EVENT_FIELD_OPTIONS.LANGUAGES,
        operators: PICK_OPERATORS,
    },
    {
        value: 'Location',
        labelKey: 'AutomationsPage.conditions.contactLocation',
        input: EVENT_FIELD_INPUT.SELECT,
        options: EVENT_FIELD_OPTIONS.LOCATIONS,
        operators: PICK_OPERATORS,
    },
    {
        value: 'Age',
        labelKey: 'AutomationsPage.conditions.age',
        input: EVENT_FIELD_INPUT.INTEGER,
        max: MAX_AGE,
        operators: [...RANGE_OPERATORS, ...PRESENCE_OPERATORS],
    },
];

const BOOLEAN_VALUES = ['true', 'false'];

const fitsFieldControl = (field, row) =>
    EVENT_FIELD_OPERATORS[field.input].some(option => option.value === row.operator) &&
    (field.input !== EVENT_FIELD_INPUT.BOOLEAN || BOOLEAN_VALUES.includes(row.value));

export const findEventField = (row, eventFields) =>
    row.kind === ROW_KIND.EVENT
        ? eventFields.find(field => field.path === row.path && fitsFieldControl(field, row)) || null
        : null;

export const findContactField = row =>
    row.kind === ROW_KIND.FIELD
        ? CONTACT_FIELDS.find(
              field =>
                  field.value === row.fieldKey &&
                  field.input &&
                  field.operators.some(option => option.value === row.operator),
          ) || null
        : null;

let rowKeySeq = 0;
const nextKey = () => `cbrow-${++rowKeySeq}`;

export const newSegmentRow = () => ({ key: nextKey(), kind: ROW_KIND.SEGMENT, negate: false, ids: [] });
export const newFieldRow = fieldKey => ({
    key: nextKey(),
    kind: ROW_KIND.FIELD,
    fieldKey,
    operator: CONTACT_FIELDS.find(field => field.value === fieldKey)?.operators?.[0].value ?? EQUALS,
    value: '',
});
export const newEventRow = () => ({ key: nextKey(), kind: ROW_KIND.EVENT, path: '', operator: EQUALS, value: '' });
export const newScenario = rows => ({ key: nextKey(), rows: rows || [newSegmentRow()] });

export const splitIds = value =>
    String(value || '')
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);

const isNegativeLabelOperator = operator => [NOT_IN, NOT_EXISTS, NOT_EQUALS, NOT_CONTAINS].includes(operator);

function conditionToRow(condition) {
    if (condition.source === CONTACT_LABEL) {
        const usesList = condition.operator === IN || condition.operator === NOT_IN;
        return {
            key: nextKey(),
            kind: ROW_KIND.SEGMENT,
            negate: isNegativeLabelOperator(condition.operator),
            ids: usesList ? splitIds(condition.value) : splitIds(condition.fieldKey),
        };
    }
    if (condition.source === CUSTOM_EVENT_DATA || condition.source === TRIGGER_ACTIVITY) {
        return {
            key: nextKey(),
            kind: ROW_KIND.EVENT,
            path: condition.fieldKey || '',
            operator: condition.operator ?? EQUALS,
            value: condition.value || '',
        };
    }
    return {
        key: nextKey(),
        kind: ROW_KIND.FIELD,
        fieldKey: condition.fieldKey || 'Email',
        operator: condition.operator ?? EQUALS,
        value: condition.value || '',
    };
}

// OR of positive segment checks reads as "in any of"; AND of negative ones as "in none of".
function collapseSegmentGroup(group) {
    const children = group.children || [];
    const conditions = group.conditions || [];
    if (children.length || !conditions.length) return null;
    if (!conditions.every(c => c.source === CONTACT_LABEL)) return null;

    if (conditions.length === 1) return conditionToRow(conditions[0]);

    const allPositive = conditions.every(c => !isNegativeLabelOperator(c.operator));
    const allNegative = conditions.every(c => isNegativeLabelOperator(c.operator));
    const logic = group.logic ?? CONDITION_LOGIC.AND;

    const union = () => {
        const rows = conditions.map(conditionToRow);
        return {
            key: nextKey(),
            kind: ROW_KIND.SEGMENT,
            negate: rows[0].negate,
            ids: [...new Set(rows.flatMap(row => row.ids))],
        };
    };

    if (logic === CONDITION_LOGIC.OR && allPositive) return union();
    if (logic === CONDITION_LOGIC.AND && allNegative) return union();
    return null;
}

const MAX_SCENARIOS = 32;

class ScenarioOverflowError extends Error {}

function groupToDnf(group) {
    const memberDnfs = [];
    for (const condition of group.conditions || []) {
        memberDnfs.push([[conditionToRow(condition)]]);
    }
    for (const child of group.children || []) {
        const collapsed = collapseSegmentGroup(child);
        memberDnfs.push(collapsed ? [[collapsed]] : groupToDnf(child));
    }

    if (!memberDnfs.length) return [];

    if ((group.logic ?? CONDITION_LOGIC.AND) === CONDITION_LOGIC.OR) {
        const flat = memberDnfs.flat();
        if (flat.length > MAX_SCENARIOS) throw new ScenarioOverflowError();
        return flat;
    }

    return memberDnfs.reduce(
        (acc, dnf) => {
            if (!dnf.length) return acc;
            if (acc.length * dnf.length > MAX_SCENARIOS) throw new ScenarioOverflowError();
            return acc.flatMap(a => dnf.map(b => [...a, ...b]));
        },
        [[]],
    );
}

// Returns null (not []) when the tree exceeds MAX_SCENARIOS, so the builder can refuse to
// render it instead of persisting an exploded rewrite.
export function normalizeScenarios(group) {
    if (!group) return [];

    let dnf;
    try {
        dnf = groupToDnf(group);
    } catch (error) {
        if (error instanceof ScenarioOverflowError) return null;
        throw error;
    }

    const scenarios = dnf.filter(rows => rows.length).map(rows => ({ key: nextKey(), rows }));

    const singlePositiveSegments =
        scenarios.length > 1 &&
        scenarios.every(s => s.rows.length === 1 && s.rows[0].kind === ROW_KIND.SEGMENT && !s.rows[0].negate);

    if (singlePositiveSegments) {
        return [
            newScenario([
                {
                    key: nextKey(),
                    kind: ROW_KIND.SEGMENT,
                    negate: false,
                    ids: [...new Set(scenarios.flatMap(s => s.rows[0].ids))],
                },
            ]),
        ];
    }

    return scenarios;
}

function rowToCondition(row) {
    if (row.kind === ROW_KIND.SEGMENT) {
        return {
            source: CONTACT_LABEL,
            fieldKey: '',
            operator: row.negate ? NOT_IN : IN,
            value: row.ids.join(','),
        };
    }
    if (row.kind === ROW_KIND.EVENT) {
        return { source: CUSTOM_EVENT_DATA, fieldKey: row.path, operator: row.operator, value: row.value };
    }
    return { source: CONTACT_FIELD, fieldKey: row.fieldKey, operator: row.operator, value: row.value };
}

export const NO_VALUE_OPERATORS = [EXISTS, NOT_EXISTS];

export function isCompleteCondition(condition) {
    if (!condition) return false;
    if (condition.source === CONTACT_LABEL) {
        if (condition.operator === IN || condition.operator === NOT_IN) {
            return splitIds(condition.value).length > 0;
        }
        return !!String(condition.fieldKey || '').trim();
    }
    if (!String(condition.fieldKey || '').trim()) return false;
    if (NO_VALUE_OPERATORS.includes(condition.operator)) return true;
    const max =
        condition.source === CONTACT_FIELD && CONTACT_FIELDS.find(field => field.value === condition.fieldKey)?.max;
    if (max) return /^\d+$/.test(String(condition.value ?? '')) && Number(condition.value) <= max;
    return !!String(condition.value || '').trim();
}

export function hasEventConditions(group) {
    if (!group) return false;
    if ((group.conditions || []).some(c => c.source === CUSTOM_EVENT_DATA || c.source === TRIGGER_ACTIVITY))
        return true;
    return (group.children || []).some(hasEventConditions);
}

export const isRowIncomplete = row => !isCompleteCondition(rowToCondition(row));

export function pruneConditionGroup(group) {
    if (!group) return null;
    const children = (group.children || []).map(pruneConditionGroup).filter(Boolean);
    const conditions = (group.conditions || []).filter(isCompleteCondition);
    if (!children.length && !conditions.length) return null;
    return { logic: group.logic || 0, conditions, children };
}

export function collectSegmentIds(group, ids = new Set()) {
    if (!group) return ids;
    for (const condition of group.conditions || []) {
        if (condition.source !== CONTACT_LABEL) continue;
        const parts =
            condition.operator === IN || condition.operator === NOT_IN
                ? splitIds(condition.value)
                : splitIds(condition.fieldKey);
        parts.forEach(part => ids.add(part));
    }
    (group.children || []).forEach(child => collectSegmentIds(child, ids));
    return ids;
}

export function hasIncompleteConditions(group) {
    if (!group) return false;
    if ((group.conditions || []).some(condition => !isCompleteCondition(condition))) return true;
    return (group.children || []).some(hasIncompleteConditions);
}

export function countConditions(group) {
    if (!group) return 0;
    return (
        (group.conditions?.length || 0) + (group.children || []).reduce((sum, child) => sum + countConditions(child), 0)
    );
}

export function scenariosToGroup(scenarios) {
    const kept = (scenarios || []).filter(scenario => scenario.rows.length);
    if (!kept.length) return null;

    if (kept.length === 1) {
        return { logic: CONDITION_LOGIC.AND, conditions: kept[0].rows.map(rowToCondition), children: [] };
    }

    return {
        logic: CONDITION_LOGIC.OR,
        conditions: [],
        children: kept.map(scenario => ({
            logic: CONDITION_LOGIC.AND,
            conditions: scenario.rows.map(rowToCondition),
            children: [],
        })),
    };
}
